import React, { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./PackageRequestPage.css";

function PackageRequestPage() {
  const location = useLocation();
  const formContainerRef = useRef(null);

  const formUrl = useMemo(() => {
    const pageParams = new URLSearchParams(location.search);

    const packageName = pageParams.get("packageName");
    const packageLocation = pageParams.get("packageLocation");

    const baseFormUrl =
      "https://frontal-firestorm-840.forms.space/form-en5hs2";

    const formParams = new URLSearchParams();

    if (packageName) {
      formParams.set("prefill.packageName", packageName);
    }

    if (packageLocation) {
      formParams.set("prefill.packageLocation", packageLocation);
    }

    const queryString = formParams.toString();

    return queryString
      ? `${baseFormUrl}?${queryString}`
      : baseFormUrl;
  }, [location.search]);

  useEffect(() => {
    const scriptId = "formspace-package-embed-script";
    const timers = [];

    let iframe = null;
    let userStartedScrolling = false;

    const previousScrollRestoration =
      "scrollRestoration" in window.history
        ? window.history.scrollRestoration
        : null;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollToTop = () => {
      if (userStartedScrolling) {
        return;
      }

      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const scheduleScrollReset = (delay) => {
      const timer = window.setTimeout(scrollToTop, delay);
      timers.push(timer);
    };

    const stopAutomaticScrollReset = () => {
      userStartedScrolling = true;

      timers.forEach((timer) => {
        window.clearTimeout(timer);
      });
    };

    // Keep the page at the top while the Formspace embed initializes.
    [
      0,
      50,
      150,
      300,
      600,
      1000,
      1600,
      2400,
      3200,
    ].forEach(scheduleScrollReset);

    window.addEventListener("wheel", stopAutomaticScrollReset, {
      once: true,
      passive: true,
    });

    window.addEventListener("touchstart", stopAutomaticScrollReset, {
      once: true,
      passive: true,
    });

    window.addEventListener("keydown", stopAutomaticScrollReset, {
      once: true,
    });

    const handleIframeLoad = () => {
      [0, 100, 350, 750, 1400, 2200].forEach(
        scheduleScrollReset
      );
    };

    const observer = new MutationObserver(() => {
      const detectedIframe =
        formContainerRef.current?.querySelector("iframe");

      if (detectedIframe && detectedIframe !== iframe) {
        iframe = detectedIframe;
        iframe.addEventListener("load", handleIframeLoad, {
          once: true,
        });

        [0, 100, 400, 900].forEach(scheduleScrollReset);
      }
    });

    if (formContainerRef.current) {
      observer.observe(formContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");

    script.id = scriptId;
    script.src = "https://formspace.com/embed.js";
    script.defer = true;
    script.crossOrigin = "anonymous";

    script.addEventListener("load", () => {
      [0, 100, 400, 900, 1600].forEach(
        scheduleScrollReset
      );
    });

    document.body.appendChild(script);

    return () => {
      observer.disconnect();

      timers.forEach((timer) => {
        window.clearTimeout(timer);
      });

      window.removeEventListener(
        "wheel",
        stopAutomaticScrollReset
      );

      window.removeEventListener(
        "touchstart",
        stopAutomaticScrollReset
      );

      window.removeEventListener(
        "keydown",
        stopAutomaticScrollReset
      );

      if (iframe) {
        iframe.removeEventListener("load", handleIframeLoad);
      }

      const scriptToRemove = document.getElementById(scriptId);

      if (scriptToRemove) {
        scriptToRemove.remove();
      }

      if (
        previousScrollRestoration &&
        "scrollRestoration" in window.history
      ) {
        window.history.scrollRestoration =
          previousScrollRestoration;
      }
    };
  }, [formUrl, location.pathname, location.search]);

  return (
  <main className="package-request-page">
    <section className="package-request-intro">
      <p className="package-request-eyebrow">
        OFFICE OUTING PACKAGES
      </p>

      <h1>Customize Your Office Outing Package</h1>

      <p className="package-request-description">
        Tell us what you’d like to keep, change, or add. Office Outing will
        review your preferences and follow up with recommended options,
        availability, and estimated pricing.
      </p>
    </section>

    <section className="package-request-note">
      <div
        className="package-request-note-icon"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 4h6.2c.53 0 1.04.21 1.41.59l8.8 8.8a2 2 0 0 1 0 2.82l-4.2 4.2a2 2 0 0 1-2.82 0l-8.8-8.8A2 2 0 0 1 4 10.2V4Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          <circle
            cx="8"
            cy="8"
            r="1.25"
            stroke="currentColor"
            strokeWidth="1.6"
          />
        </svg>
      </div>

      <div>
        <h2>Submitting this form is free.</h2>

        <p>
          Package costs and any optional planning fees will be clearly
          presented before you approve anything.
        </p>
      </div>
    </section>

    <section
      ref={formContainerRef}
      className="package-form-container"
    >
      <div
        key={formUrl}
        data-formspace={formUrl}
      />
    </section>
  </main>
);
}

export default PackageRequestPage;
